import { Component, Input } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { IOptions } from 'src/app/shared/components/radio-group/radio-group.component';
import { CarerReliabilityEvaluation } from 'src/app/shared/models/gql/carer-reliability-evaluation.model';
import { ReliabilityFeedback } from 'src/app/shared/models/gql/carer-reliability.model';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { CancelEvaluationConfirmationModal } from '../cancel-evaluation-confirmation-modal/cancel-evaluation-confirmation-modal';
import { ReliabilityAssessmentService } from '../services/reliability-assessment.service';

@Component({
  selector: 'app-one-off-evaluation-modal',
  templateUrl: './one-off-evaluation-modal.component.html',
  styleUrls: ['./one-off-evaluation-modal.component.scss'],
})
export class OneOffEvaluationModalComponent {
  // Reactive form for Binding controls
  form: UntypedFormGroup;

  // Options for radio buttons
  radioGroupOptions: IOptions[] = [
    { value: true, name: 'Yes' },
    { value: false, name: 'No' },
  ];

  // assessment object
  assessmentData: CarerReliabilityEvaluation;

  // id of the assessment
  @Input() assessmentId: string;

  constructor(
    public modalCtrl: ModalController,
    protected fb: UntypedFormBuilder,
    private assessmentService: ReliabilityAssessmentService,
    private utilityService: UtilityService,
  ) {
    this.setupFormFields();
    this.getCarerReliabilityAssessmentData();
  }

  /**
   * Initialise form group and set up form fields.
   */
  setupFormFields() {
    this.form = this.fb.group({
      isShowedUp: [null, [Validators.required]],
      isOnTime: [null, [Validators.required]],
      isTaskCompleted: [null, [Validators.required]],
      otherFeedback: [null],
    });
  }

  /**
   * Get Assessment data.
   */
  async getCarerReliabilityAssessmentData() {
    const requestLoader = await this.utilityService.showLoading();
    try {
      const resData = await this.assessmentService.getReliabilityAssessmentData(this.assessmentId);
      if (resData.agreement) {
        this.assessmentData = resData;
      }
    } catch (error) {
      this.utilityService.showErrorAlert('Unable to perform action. Please try again later');
    } finally {
      this.utilityService.hideLoading(requestLoader);
    }
  }

  /**
   * Update carerReliabilityAssessment when form is submitted.
   */
  async submitEvaluation() {
    if (this.form.valid) {
      const requestLoader = await this.utilityService.showLoading();
      try {
        const value = this.form.value;

        const requestObj: ReliabilityFeedback = {
          id: this.assessmentId,
          carerReliabilityAttributes: {
            isShowedUp: value.isShowedUp,
            isOnTime: value.isOnTime,
            isTaskCompleted: value.isTaskCompleted,
            otherFeedback: value.otherFeedback,
            scheduledSessions: 0, // scheduledSessions is unknown for monthly forms
          },
        };

        const resData = await this.assessmentService.postCarerReliabilityFeedback(requestObj);

        if (resData) {
          await this.utilityService.showAlert('Success', 'Your evaluation has been submitted');
          this.modalCtrl.dismiss();
          this.assessmentService.refresh();
        }
      } catch (error) {
        this.utilityService.showErrorAlert('Unable to perform action. Please try again later');
      } finally {
        this.utilityService.hideLoading(requestLoader);
      }
    } else {
      this.form.markAllAsTouched();
    }
  }

  /**
   * Open confirmation modal to ask user if they are sure they want to exit, and gives them an option to opt out.
   */
  async openCancelModal() {
    const confirmModal = await this.modalCtrl.create({
      component: CancelEvaluationConfirmationModal,
      cssClass: 'fullpage-modal',
      componentProps: {
        workerName: this.assessmentData?.agreement?.otherUser?.firstName,
      },
    });

    // https://github.com/ionic-team/ionic-framework/issues/26872
    confirmModal.shadowRoot.querySelector('.modal-wrapper').removeAttribute('aria-modal');

    confirmModal.onDidDismiss().then(({ data }) => {
      if (data?.optout) {
        // when optout is true then close modal and refresh the dashboard page
        this.modalCtrl.dismiss(null, null, 'one-off-evaluation-modal');
        this.assessmentService.refresh();
      } else if (data?.doItLater) {
        // when doItLater is true, just close the modal (no refresh)
        this.modalCtrl.dismiss(null, null, 'one-off-evaluation-modal');
      }
    });
    await confirmModal.present();
  }
}
